import { forwardRef } from 'react';
import classNames from 'classnames';
import { SpacingType } from '@vds/core';
import './Stack.scss';

export interface StackProps extends React.HTMLAttributes<HTMLElement> {
  component?: React.ElementType;
  display?: 'flex' | 'inline-flex';
  className?: string;
  children?: React.ReactNode;
  alignItems?: React.CSSProperties['alignItems'];
  alignContent?: React.CSSProperties['alignContent'];
  justifyContent?: React.CSSProperties['justifyContent'];
  justifyItems?: React.CSSProperties['justifyItems'];
  flexWrap?: React.CSSProperties['flexWrap'];
  flexDirection?: React.CSSProperties['flexDirection'];
  flexGrow?: React.CSSProperties['flexGrow'];
  flexShrink?: React.CSSProperties['flexShrink'];
  flexBasis?: React.CSSProperties['flexBasis'];
  alignSelf?: React.CSSProperties['alignSelf'];
  order?: React.CSSProperties['order'];
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
  gap?: SpacingType;
}

/**
 * Stack Component
 * @param component - The component used for the root node. Either a string to use a HTML element or a component.
 * @param display - CSS Property - flex or inline-flex.
 * @param className - Additional classNames for the component.
 * @param children - Any React.ReactNode Element.
 * @param alignItems - CSS Property.
 * @param alignContent - CSS Property.
 * @param justifyContent - CSS Property.
 * @param justifyItems - CSS Property.
 * @param flexWrap - CSS Property.
 * @param flexDirection - CSS Property.
 * @param flexGrow - CSS Property.
 * @param flexShrink - CSS Property.
 * @param flexBasis - CSS Property.
 * @param alignSelf - CSS Property.
 * @param order - CSS Property.
 * @param height - CSS Property.
 * @param width - CSS Property.
 * @param gap - If set, children will be have a gutter space each other. This prop uses tokens.
 */
export const Stack = forwardRef<React.ReactNode, StackProps>(
  (
    {
      component: Component = 'div',
      className,
      children,
      alignItems,
      alignContent,
      justifyContent,
      justifyItems,
      flexWrap,
      flexDirection,
      flexGrow,
      flexShrink,
      flexBasis,
      alignSelf,
      order,
      display = 'flex',
      gap,
      height,
      width,
      ...rest
    },
    ref,
  ) => {
    return (
      <Component
        className={classNames('vds-stack', { [`vds-stack--gap-${gap}`]: gap }, className)}
        ref={ref}
        style={{
          alignItems,
          alignContent,
          justifyContent,
          justifyItems,
          flexWrap,
          flexDirection,
          flexGrow,
          flexShrink,
          flexBasis,
          alignSelf,
          order,
          display,
          height,
          width,
        }}
        {...rest}>
        {children}
      </Component>
    );
  },
);
